// Geometry generators for platonic solids + truncated icosahedron.
// Every polyhedron returns { vertices: [[x,y,z],...], faces: [[i0,i1,...], ...] }
// with vertices normalized to the unit sphere.

const PHI = (1 + Math.sqrt(5)) / 2;

const norm = (v) => {
  const l = Math.hypot(v[0], v[1], v[2]) || 1;
  return [v[0] / l, v[1] / l, v[2] / l];
};

const sub = (a, b) => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
const cross = (a, b) => [
  a[1] * b[2] - a[2] * b[1],
  a[2] * b[0] - a[0] * b[2],
  a[0] * b[1] - a[1] * b[0],
];
const dot = (a, b) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];

// Sort a set of neighbor vertex indices around a center vertex c (on the sphere)
// by angle in the tangent plane. Returns them in CCW order looking from outside.
function sortAroundVertex(baseVerts, cIdx, neighbors) {
  const c = baseVerts[cIdx];
  let right = Math.abs(c[0]) > 0.9 ? [0, 1, 0] : [1, 0, 0];
  const d = dot(c, right);
  right = norm([right[0] - d * c[0], right[1] - d * c[1], right[2] - d * c[2]]);
  const forward = cross(c, right);
  return [...neighbors]
    .map((n) => {
      const p = baseVerts[n];
      return { n, angle: Math.atan2(dot(p, forward), dot(p, right)) };
    })
    .sort((a, b) => a.angle - b.angle)
    .map((x) => x.n);
}

// Ensure every face is CCW when viewed from outside (normal pointing away from origin).
function orientOutward(vertices, faces) {
  return faces.map((f) => {
    const a = vertices[f[0]];
    const b = vertices[f[1]];
    const c = vertices[f[2]];
    const n = cross(sub(b, a), sub(c, a));
    // face centroid direction
    if (dot(n, a) < 0) return [...f].reverse();
    return f;
  });
}

export function tetrahedron() {
  const raw = [
    [1, 1, 1],
    [-1, -1, 1],
    [-1, 1, -1],
    [1, -1, -1],
  ];
  const vertices = raw.map(norm);
  const faces = orientOutward(vertices, [
    [0, 1, 2],
    [0, 3, 1],
    [0, 2, 3],
    [1, 3, 2],
  ]);
  return { vertices, faces };
}

export function octahedron() {
  const vertices = [
    [1, 0, 0],
    [-1, 0, 0],
    [0, 1, 0],
    [0, -1, 0],
    [0, 0, 1],
    [0, 0, -1],
  ].map(norm);
  const faces = orientOutward(vertices, [
    [0, 2, 4], [2, 1, 4], [1, 3, 4], [3, 0, 4],
    [2, 0, 5], [1, 2, 5], [3, 1, 5], [0, 3, 5],
  ]);
  return { vertices, faces };
}

export function icosahedron() {
  const P = PHI;
  const raw = [
    [-1, P, 0], [1, P, 0], [-1, -P, 0], [1, -P, 0],
    [0, -1, P], [0, 1, P], [0, -1, -P], [0, 1, -P],
    [P, 0, -1], [P, 0, 1], [-P, 0, -1], [-P, 0, 1],
  ];
  const vertices = raw.map(norm);
  const faces = orientOutward(vertices, [
    [0, 11, 5], [0, 5, 1], [0, 1, 7], [0, 7, 10], [0, 10, 11],
    [1, 5, 9], [5, 11, 4], [11, 10, 2], [10, 7, 6], [7, 1, 8],
    [3, 9, 4], [3, 4, 2], [3, 2, 6], [3, 6, 8], [3, 8, 9],
    [4, 9, 5], [2, 4, 11], [6, 2, 10], [8, 6, 7], [9, 8, 1],
  ]);
  return { vertices, faces };
}

// Dual of a convex polyhedron: face centroids become new vertices;
// each old vertex becomes a face made of its incident-face centroids (ordered).
export function dual(base) {
  const centroids = base.faces.map((f) => {
    const c = [0, 0, 0];
    f.forEach((i) => {
      c[0] += base.vertices[i][0];
      c[1] += base.vertices[i][1];
      c[2] += base.vertices[i][2];
    });
    return norm(c);
  });

  const facesForVertex = base.vertices.map((_, vi) =>
    base.faces
      .map((f, fi) => (f.includes(vi) ? fi : -1))
      .filter((x) => x >= 0),
  );

  const newFaces = base.vertices.map((v, vi) => {
    const incident = facesForVertex[vi];
    // Sort incident face indices by angle around v in tangent plane using centroids
    let right = Math.abs(v[0]) > 0.9 ? [0, 1, 0] : [1, 0, 0];
    const d = dot(v, right);
    right = norm([right[0] - d * v[0], right[1] - d * v[1], right[2] - d * v[2]]);
    const forward = cross(v, right);
    return incident
      .map((fi) => {
        const p = centroids[fi];
        return { fi, angle: Math.atan2(dot(p, forward), dot(p, right)) };
      })
      .sort((a, b) => a.angle - b.angle)
      .map((x) => x.fi);
  });

  return { vertices: centroids, faces: orientOutward(centroids, newFaces) };
}

export function cube() {
  return dual(octahedron());
}

export function dodecahedron() {
  return dual(icosahedron());
}

// Truncate a convex polyhedron by cutting each vertex at t along each incident edge.
// t = 1/3 gives the classic Archimedean truncation (regular hexagons from triangles).
export function truncate(base, t = 1 / 3) {
  const { vertices, faces } = base;

  const edgeKey = (a, b) => (a < b ? `${a}_${b}` : `${b}_${a}`);
  const edges = new Map(); // key -> { a,b, nearAIdx, nearBIdx }

  const newVerts = [];

  const ensureEdge = (a, b) => {
    const k = edgeKey(a, b);
    if (!edges.has(k)) {
      const va = vertices[a];
      const vb = vertices[b];
      const nearA = norm([
        va[0] + (vb[0] - va[0]) * t,
        va[1] + (vb[1] - va[1]) * t,
        va[2] + (vb[2] - va[2]) * t,
      ]);
      const nearB = norm([
        va[0] + (vb[0] - va[0]) * (1 - t),
        va[1] + (vb[1] - va[1]) * (1 - t),
        va[2] + (vb[2] - va[2]) * (1 - t),
      ]);
      const nearAIdx = newVerts.length;
      newVerts.push(nearA);
      const nearBIdx = newVerts.length;
      newVerts.push(nearB);
      edges.set(k, { a, b, nearAIdx, nearBIdx });
    }
    return edges.get(k);
  };

  faces.forEach((f) => {
    for (let i = 0; i < f.length; i++) {
      ensureEdge(f[i], f[(i + 1) % f.length]);
    }
  });

  const nearIdx = (v, other) => {
    const e = edges.get(edgeKey(v, other));
    return e.a === v ? e.nearAIdx : e.nearBIdx;
  };

  // Each old face becomes a bigger face (2n vertices)
  const newFaces = [];
  faces.forEach((f) => {
    const face = [];
    for (let i = 0; i < f.length; i++) {
      const cur = f[i];
      const next = f[(i + 1) % f.length];
      face.push(nearIdx(cur, next));
      face.push(nearIdx(next, cur));
    }
    newFaces.push(face);
  });

  // Each old vertex becomes a face (n-gon) from its incident-edge near-v points.
  vertices.forEach((v, vi) => {
    const neighborSet = new Set();
    faces.forEach((f) => {
      const i = f.indexOf(vi);
      if (i >= 0) {
        neighborSet.add(f[(i + 1) % f.length]);
        neighborSet.add(f[(i - 1 + f.length) % f.length]);
      }
    });
    const ordered = sortAroundVertex(vertices, vi, neighborSet);
    newFaces.push(ordered.map((n) => nearIdx(vi, n)));
  });

  return { vertices: newVerts, faces: orientOutward(newVerts, newFaces) };
}

export function truncatedIcosahedron() {
  return truncate(icosahedron(), 1 / 3);
}

// Star Ball — UEFA Champions League "Finale" style. 12 five-pointed stars
// at the icosahedron vertices tile with 20 irregular hexagons at the
// icosahedron face centers. Star tips sit at the midpoints of the 30
// icosahedron edges — every tip is shared with the neighbouring star
// (tip-to-tip) AND is a corner of the 2 hexagons on either side.
export function starBall() {
  const base = icosahedron();
  const V = base.vertices;
  const F = base.faces;
  const outVerts = [];

  // Edge midpoints (30 total, unique per unordered vertex pair).
  const edgeMid = new Map();
  const midOf = (a, b) => {
    const k = a < b ? `${a}_${b}` : `${b}_${a}`;
    if (edgeMid.has(k)) return edgeMid.get(k);
    const va = V[a];
    const vb = V[b];
    const m = [(va[0] + vb[0]) / 2, (va[1] + vb[1]) / 2, (va[2] + vb[2]) / 2];
    const L = Math.hypot(m[0], m[1], m[2]) || 1;
    const idx = outVerts.length;
    outVerts.push([m[0] / L, m[1] / L, m[2] / L]);
    edgeMid.set(k, idx);
    return idx;
  };

  // Inner star points (60 total, one per (vertex, incident face) pair).
  // Each sits between the tip corners of that star, biased toward the
  // vertex so the star has crisp arms that meet at the tips.
  const innerPt = new Map();
  const INNER_T = 0.55; // 0 = point at vertex, 1 = at face centroid
  const makeInner = (vi, fi) => {
    const k = `${vi}_${fi}`;
    if (innerPt.has(k)) return innerPt.get(k);
    const face = F[fi];
    let cx = 0, cy = 0, cz = 0;
    for (const j of face) { cx += V[j][0]; cy += V[j][1]; cz += V[j][2]; }
    cx /= 3; cy /= 3; cz /= 3;
    const cL = Math.hypot(cx, cy, cz) || 1;
    const centroid = [cx / cL, cy / cL, cz / cL];
    const vv = V[vi];
    const p = [
      vv[0] + (centroid[0] - vv[0]) * INNER_T,
      vv[1] + (centroid[1] - vv[1]) * INNER_T,
      vv[2] + (centroid[2] - vv[2]) * INNER_T,
    ];
    const L = Math.hypot(p[0], p[1], p[2]) || 1;
    const idx = outVerts.length;
    outVerts.push([p[0] / L, p[1] / L, p[2] / L]);
    innerPt.set(k, idx);
    return idx;
  };

  // Per-vertex list of incident faces (each icosahedron vertex has 5).
  const vertexToFaces = V.map(() => []);
  F.forEach((face, fi) => face.forEach((vi) => vertexToFaces[vi].push(fi)));

  const outFaces = [];

  // HEXAGON panels — one per icosahedron face. Ring order goes round the
  // face alternating tip (edge midpoint) and inner-star point:
  //   mid(a,b), inner(a,f), mid(a,c), inner(c,f), mid(b,c), inner(b,f)
  F.forEach((face, fi) => {
    const [a, b, c] = face;
    const hex = [
      midOf(a, b),
      makeInner(a, fi),
      midOf(a, c),
      makeInner(c, fi),
      midOf(b, c),
      makeInner(b, fi),
    ];
    outFaces.push(hex);
  });

  // STAR panels — one per icosahedron vertex. Walk the 5 incident faces
  // cyclically; each face contributes its far edge midpoint (tip) and
  // its own inner-star point.
  V.forEach((_, vi) => {
    const incident = vertexToFaces[vi];
    // Order incident faces so consecutive ones share an edge at vi.
    const ordered = [incident[0]];
    const used = new Set([incident[0]]);
    while (ordered.length < incident.length) {
      const prev = F[ordered[ordered.length - 1]];
      let next = -1;
      for (const fi of incident) {
        if (used.has(fi)) continue;
        const shared = F[fi].filter((v) => prev.includes(v));
        if (shared.length === 2) { next = fi; break; }
      }
      if (next < 0) break;
      ordered.push(next);
      used.add(next);
    }
    const star = [];
    for (let k = 0; k < ordered.length; k++) {
      const fi = ordered[k];
      const fiPrev = ordered[(k - 1 + ordered.length) % ordered.length];
      // Edge shared between fi and fiPrev at vertex vi — that's where the
      // arm ENTERS face fi.
      const shared = F[fi].find((v) => v !== vi && F[fiPrev].includes(v));
      // Tip at that shared edge midpoint, then the inner point that sits
      // INSIDE face fi — so the arc (tip → inner → next tip) is entirely
      // contained in face fi, matching the hex-panel edges on that face.
      star.push(midOf(vi, shared));
      star.push(makeInner(vi, fi));
    }
    outFaces.push(star);
  });

  return { vertices: outVerts, faces: orientOutward(outVerts, outFaces) };
}

// Truncated octahedron (Archimedean solid) — 6 squares + 8 hexagons = 14 panels.
export function truncatedOctahedron() {
  return truncate(octahedron(), 1 / 3);
}

// One level of edge subdivision on the truncated octahedron — 14 panels
// (6 squares + 8 hexagons) with midpoint control handles on every seam.
export function truncatedOctahedronPlus() {
  return subdivideEdges(truncatedOctahedron());
}

// Two levels of edge subdivision — 14 panels with hundreds more control
// points for extra-smooth curved seams on the truncated-octahedron ball.
export function truncatedOctahedronUltra() {
  return subdivideEdges(subdivideEdges(truncatedOctahedron()));
}

// Subdivide every edge with its midpoint (normalized to the sphere) and
// rewrite each n-gon face into a 2n-gon that alternates original corners with
// edge midpoints. This produces smoothly curved panels reminiscent of modern
// thermally-bonded match balls (Brazuca, Trionda).
export function subdivideEdges(base) {
  const { vertices, faces } = base;
  const midIdx = new Map();
  const key = (a, b) => (a < b ? `${a}_${b}` : `${b}_${a}`);
  const outVerts = vertices.map((v) => [v[0], v[1], v[2]]);

  const getMid = (a, b) => {
    const k = key(a, b);
    if (midIdx.has(k)) return midIdx.get(k);
    const va = vertices[a];
    const vb = vertices[b];
    const m = [(va[0] + vb[0]) / 2, (va[1] + vb[1]) / 2, (va[2] + vb[2]) / 2];
    const l = Math.hypot(m[0], m[1], m[2]) || 1;
    const i = outVerts.length;
    outVerts.push([m[0] / l, m[1] / l, m[2] / l]);
    midIdx.set(k, i);
    return i;
  };

  const outFaces = faces.map((f) => {
    const nf = [];
    for (let i = 0; i < f.length; i++) {
      nf.push(f[i]);
      nf.push(getMid(f[i], f[(i + 1) % f.length]));
    }
    return nf;
  });

  return { vertices: outVerts, faces: orientOutward(outVerts, outFaces) };
}

// Trionda-inspired 4-panel: three levels of edge subdivision on the tetrahedron
// give plenty of control points along every panel boundary for artistic shaping.
export function triondaFour() {
  return subdivideEdges(subdivideEdges(subdivideEdges(tetrahedron())));
}

// Brazuca-inspired 6-panel: three levels of subdivision on the cube for the
// same reason.
export function brazucaSix() {
  return subdivideEdges(subdivideEdges(subdivideEdges(cube())));
}

// One-level subdivision variants used for the "Cube" and "Octahedron"
// templates so the user has extra vertices to shape each panel.
export function cubeSubdivided() {
  return subdivideEdges(cube());
}

export function octahedronSubdivided() {
  return subdivideEdges(octahedron());
}

// Octahedron with two levels of edge subdivision — 8 triangular panels
// (12-gons after subdivision) with 66 control points so users can finely
// sculpt each panel boundary.
export function octahedronPlus() {
  return subdivideEdges(subdivideEdges(octahedron()));
}

// Three levels of edge subdivision — 8 panels with 258 handles across
// 24-gon boundaries. For sculptors who want maximum control per panel.
export function octahedronUltra() {
  return subdivideEdges(subdivideEdges(subdivideEdges(octahedron())));
}

// Dodecahedron with two levels of edge subdivision — gives 110 vertex handles
// along its 12 pentagonal panels (20-gons after subdivision) so users can
// finely sculpt each panel boundary.
export function dodecahedronPlus() {
  return subdivideEdges(subdivideEdges(dodecahedron()));
}

// Same but with three levels — 230 handles per ball, 40-gon panels.
export function dodecahedronUltra() {
  return subdivideEdges(subdivideEdges(subdivideEdges(dodecahedron())));
}

// Icosahedron with two levels of edge subdivision — 102 handles across
// 20 triangular-region panels (12-gons after subdivision).
export function icosahedronPlus() {
  return subdivideEdges(subdivideEdges(icosahedron()));
}

// Truncated icosahedron ("Classic 32") with one level of edge subdivision —
// 150 handles distributed along the pentagon and hexagon boundaries.
export function truncatedIcosahedronPlus() {
  return subdivideEdges(truncatedIcosahedron());
}

// Two levels of edge subdivision — 32 panels with hundreds more control
// points for sculpting extra-smooth curved seams on the classic ball.
export function truncatedIcosahedronUltra() {
  return subdivideEdges(subdivideEdges(truncatedIcosahedron()));
}

// Star Ball+ — same 32-panel topology as Star Ball but with every edge
// subdivided once so each star arm and hexagon side has a midpoint
// control handle for finer sculpting.
export function starBallPlus() {
  return subdivideEdges(starBall());
}

// Stripes & Spots — Nike Total 90 / Barclays-inspired: one circular
// "spot" panel on the front (north pole), one on the back (south pole),
// and N equal-width stripes wrapping meridian-style around the ball to
// connect the two spots. Each cap is an N-gon and each stripe is a quad
// with 3 extra control points inserted along every seam, so the ball
// has plenty of vertex handles for shaping into rounded spots and
// smooth stripe curves.
//
// axis = 'y' → caps on top and bottom of the ball (poles along Y).
// axis = 'z' → caps on front and back (poles along Z) — reads as a big
//              round spot right at the front of the default viewport.
function stripesTemplate({ n = 12, latDeg = 55, axis = 'y' } = {}) {
  const LAT = (latDeg * Math.PI) / 180;
  const cosLat = Math.cos(LAT);
  const sinLat = Math.sin(LAT);
  const verts = [];
  // Push a point on the sphere in the chosen orientation. Local frame:
  // "up" = pole direction, and lon = 0 sits on the ball's +X (Y-axis) or
  // +X (Z-axis) so the ball reads with a stripe seam at the front.
  const pushSpherical = (lat, lon) => {
    const cLat = Math.cos(lat);
    const sLat = Math.sin(lat);
    const cLon = Math.cos(lon);
    const sLon = Math.sin(lon);
    let p;
    if (axis === 'y') p = [cLat * cLon, sLat, cLat * sLon];
    else p = [cLat * cLon, cLat * sLon, sLat]; // axis === 'z'
    verts.push(p);
    return verts.length - 1;
  };
  // Cap rings — an N-gon plus 2 extra midpoints per side (subdivision=2) so
  // the cap smooths into a proper circle after just one double-click.
  const CAP_SUBDIV = 2;
  const northCap = [];
  const southCap = [];
  const stripeTop = []; // stripeTop[k] = { corner: idx, mids: [idx1, idx2, ...] } for the k-th stripe's top-edge subdivision
  const stripeBot = [];
  const CAP_LAT_N = LAT;
  const CAP_LAT_S = -LAT;
  const totalCapSteps = n * (CAP_SUBDIV + 1); // total vertices around the cap ring
  for (let s = 0; s < totalCapSteps; s++) {
    const lon = (s / totalCapSteps) * 2 * Math.PI - Math.PI / 2;
    northCap.push(pushSpherical(CAP_LAT_N, lon));
    southCap.push(pushSpherical(CAP_LAT_S, lon));
  }
  // Extra vertex rings on the stripe sides — 2 latitudes between the two
  // caps so every stripe corner has 3 control points on each vertical
  // seam (top-corner, 2 mid-verts, bottom-corner).
  const SIDE_STEPS = 3; // 3 divisions → 2 intermediate rings
  const sideRings = [];
  for (let r = 1; r < SIDE_STEPS; r++) {
    const t = r / SIDE_STEPS;
    const lat = CAP_LAT_N + (CAP_LAT_S - CAP_LAT_N) * t;
    const ring = [];
    for (let k = 0; k < n; k++) {
      const lon = (k / n) * 2 * Math.PI - Math.PI / 2;
      ring.push(pushSpherical(lat, lon));
    }
    sideRings.push(ring);
  }
  const faces = [];
  // Caps — the two big N-gon panels (with CAP_SUBDIV midpoints per side).
  faces.push([...northCap]);
  faces.push([...southCap]);
  // Stripe quads (long, thin panels with midpoints on all 4 sides).
  //   Top edge:  northCap sub-slice (CAP_SUBDIV+2 vertices).
  //   Bottom:    southCap sub-slice, reversed.
  //   Left side: [top-left, ...sideRings vertices at k, bottom-left]
  //   Right:     [bottom-right, ...sideRings at k+1 (reversed), top-right]
  for (let k = 0; k < n; k++) {
    const perSlice = CAP_SUBDIV + 1;
    const topStart = k * perSlice;
    const topEnd = ((k + 1) * perSlice) % totalCapSteps;
    // Top edge, north-cap arc from topStart to topEnd (going forward).
    const top = [];
    for (let s = 0; s <= perSlice; s++) top.push(northCap[(topStart + s) % totalCapSteps]);
    // Bottom edge, south-cap arc from topEnd to topStart (reversed).
    const bot = [];
    for (let s = 0; s <= perSlice; s++) bot.push(southCap[(topEnd - s + totalCapSteps) % totalCapSteps]);
    // Right side: descending intermediate rings at index (k+1) % n.
    const rightSide = sideRings.map((ring) => ring[(k + 1) % n]);
    // Left side: descending intermediate rings at index k.
    const leftSide = sideRings.map((ring) => ring[k]);
    // Assemble the quad boundary CCW when viewed from outside.
    const face = [
      ...top,               // top-left → mids → top-right
      ...rightSide,         // descending on the right
      ...bot,               // bottom-right → mids → bottom-left
      ...leftSide.slice().reverse(), // ascending on the left
    ];
    faces.push(face);
  }
  return { vertices: verts, faces: orientOutward(verts, faces) };
}

export function stripesAndSpots() {
  return stripesTemplate({ n: 12, latDeg: 55, axis: 'y' });
}

// Front-spot variant — front and back polar caps on the Z axis with
// latitudinal stripes wrapping horizontally around the ball (running
// perpendicular to the pole axis rather than pole-to-pole). Each band is
// split into 2 half-rings so every stripe panel is a simple simply-
// connected polygon (no annular faces), while the top/bottom of every
// stripe is a full arc of the cap ring for smooth colour banding.
export function stripesAndSpotsFront() {
  const N = 16;      // cap-ring resolution (bigger = rounder spot)
  const BANDS = 6;   // latitudinal stripes between the two caps
  const SEG = 2;     // segments per band (2 halves — minimises seams)
  const LAT = (55 * Math.PI) / 180;
  // Angle FROM the +Z pole. capAngleFront ≈ π/2 − LAT (small ⇒ small
  // front cap). Bands stretch from capAngleFront down to π − capAngleFront.
  const capAngleFront = Math.PI / 2 - LAT;
  const capAngleBack = Math.PI - capAngleFront;
  const latLevels = [];
  for (let i = 0; i <= BANDS; i++) {
    latLevels.push(capAngleFront + (capAngleBack - capAngleFront) * (i / BANDS));
  }
  const verts = [];
  // For each latitude level, place N vertices around the Z axis.
  const rings = latLevels.map((angFromZ) => {
    const sinA = Math.sin(angFromZ);
    const cosA = Math.cos(angFromZ);
    const ring = [];
    for (let k = 0; k < N; k++) {
      const lon = (k / N) * 2 * Math.PI;
      const cosL = Math.cos(lon);
      const sinL = Math.sin(lon);
      ring.push(verts.length);
      verts.push([sinA * cosL, sinA * sinL, cosA]);
    }
    return ring;
  });
  const faces = [];
  // Front cap (facing +Z, first ring).
  faces.push([...rings[0]]);
  // Back cap (facing −Z, last ring reversed).
  faces.push([...rings[BANDS]].reverse());
  // Latitudinal band panels — each band split into `SEG` half-rings that
  // share seam vertices with their neighbours so the panels tile the
  // sphere without gaps.
  for (let i = 0; i < BANDS; i++) {
    const top = rings[i];
    const bot = rings[i + 1];
    for (let s = 0; s < SEG; s++) {
      const start = Math.floor((s * N) / SEG);
      const end = Math.floor(((s + 1) * N) / SEG);
      const face = [];
      // Top edge left-to-right.
      for (let k = start; k <= end; k++) face.push(top[k % N]);
      // Bottom edge right-to-left.
      for (let k = end; k >= start; k--) face.push(bot[k % N]);
      faces.push(face);
    }
  }
  return { vertices: verts, faces: orientOutward(verts, faces) };
}

// Geodesic subdivision of an icosahedron for arbitrary panel counts.
// frequency=1 -> 20, 2 -> 80, 3 -> 180. We use it for "custom" counts.
export function geodesic(frequency = 2) {
  const base = icosahedron();
  const vertsMap = new Map();
  const getIdx = (v) => {
    const key = v.map((x) => x.toFixed(6)).join(',');
    if (vertsMap.has(key)) return vertsMap.get(key);
    const i = vertsMap.size;
    vertsMap.set(key, i);
    return i;
  };
  const outVerts = [];
  const push = (v) => {
    const nv = norm(v);
    const key = nv.map((x) => x.toFixed(6)).join(',');
    if (vertsMap.has(key)) return vertsMap.get(key);
    const i = outVerts.length;
    vertsMap.set(key, i);
    outVerts.push(nv);
    return i;
  };
  const outFaces = [];
  base.faces.forEach((f) => {
    const A = base.vertices[f[0]];
    const B = base.vertices[f[1]];
    const C = base.vertices[f[2]];
    // grid of frequency+1 rows
    const grid = [];
    for (let i = 0; i <= frequency; i++) {
      const row = [];
      for (let j = 0; j <= frequency - i; j++) {
        const k = frequency - i - j;
        const p = [
          (A[0] * i + B[0] * j + C[0] * k) / frequency,
          (A[1] * i + B[1] * j + C[1] * k) / frequency,
          (A[2] * i + B[2] * j + C[2] * k) / frequency,
        ];
        row.push(push(p));
      }
      grid.push(row);
    }
    for (let i = 0; i < frequency; i++) {
      for (let j = 0; j < frequency - i; j++) {
        outFaces.push([grid[i][j], grid[i + 1][j], grid[i][j + 1]]);
        if (j < frequency - i - 1) {
          outFaces.push([grid[i + 1][j], grid[i + 1][j + 1], grid[i][j + 1]]);
        }
      }
    }
  });
  return { vertices: outVerts, faces: orientOutward(outVerts, outFaces) };
}

// Public factory
export function buildTemplate(id) {
  switch (id) {
    case 'trionda':
      return { ...triondaFour(), meta: { id, name: 'Triangle', panels: 4 } };
    case 'cube':
      return { ...cubeSubdivided(), meta: { id, name: 'Cube', panels: 6 } };
    case 'brazuca':
      return { ...brazucaSix(), meta: { id, name: 'Cube+', panels: 6 } };
    case 'octahedron':
      return { ...octahedronSubdivided(), meta: { id, name: 'Octahedron', panels: 8 } };
    case 'octahedron_plus':
      return { ...octahedronPlus(), meta: { id, name: 'Octahedron+', panels: 8 } };
    case 'octahedron_ultra':
      return { ...octahedronUltra(), meta: { id, name: 'Octahedron+ Ultra', panels: 8 } };
    case 'dodecahedron':
      return { ...dodecahedron(), meta: { id, name: 'Dodecahedron', panels: 12 } };
    case 'dodecahedron_plus':
      return { ...dodecahedronPlus(), meta: { id, name: 'Dodeca+', panels: 12 } };
    case 'dodecahedron_ultra':
      return { ...dodecahedronUltra(), meta: { id, name: 'Dodeca+ Ultra', panels: 12 } };
    case 'truncated_octahedron':
      return { ...truncatedOctahedron(), meta: { id, name: 'Truncated Octa', panels: 14 } };
    case 'truncated_octahedron_plus':
      return { ...truncatedOctahedronPlus(), meta: { id, name: 'Truncated Octa+', panels: 14 } };
    case 'truncated_octahedron_ultra':
      return { ...truncatedOctahedronUltra(), meta: { id, name: 'Truncated Octa+ Ultra', panels: 14 } };
    case 'icosahedron':
      return { ...icosahedron(), meta: { id, name: 'Icosahedron', panels: 20 } };
    case 'icosahedron_plus':
      return { ...icosahedronPlus(), meta: { id, name: 'Icosahedron+', panels: 20 } };
    case 'truncated_icosahedron_plus':
      return {
        ...truncatedIcosahedronPlus(),
        meta: { id, name: 'Classic 32+', panels: 32 },
      };
    case 'truncated_icosahedron_ultra':
      return {
        ...truncatedIcosahedronUltra(),
        meta: { id, name: 'Classic 32+ Ultra', panels: 32 },
      };
    case 'star_ball':
      return { ...starBall(), meta: { id, name: 'Star Ball', panels: 32 } };
    case 'star_ball_plus':
      return { ...starBallPlus(), meta: { id, name: 'Star Ball+', panels: 32 } };
    case 'stripes_and_spots':
      return { ...stripesAndSpots(), meta: { id, name: 'Stripes & Spots', panels: 14 } };
    case 'stripes_and_spots_front':
      return { ...stripesAndSpotsFront(), meta: { id, name: 'Front Spot', panels: 14 } };
    case 'truncated_icosahedron':
    default:
      return {
        ...truncatedIcosahedron(),
        meta: { id: 'truncated_icosahedron', name: 'Classic 32-Panel', panels: 32 },
      };
  }
}

export const TEMPLATES = [
  { id: 'trionda', name: 'Triangle', panels: 4, description: '4 curved panels · 2026 WC-inspired' },
  { id: 'cube', name: 'Cube', panels: 6, description: '6 square panels' },
  { id: 'brazuca', name: 'Cube+', panels: 6, description: '6 rounded panels · 2014 WC-inspired' },
  { id: 'octahedron', name: 'Octahedron', panels: 8, description: '8 triangular panels' },
  { id: 'octahedron_plus', name: 'Octahedron+', panels: 8, description: '8 panels · 42 control points' },
  { id: 'octahedron_ultra', name: 'Octahedron+ Ultra', panels: 8, description: '8 panels · 90 control points' },
  { id: 'dodecahedron', name: 'Dodecahedron', panels: 12, description: '12 pentagonal panels' },
  { id: 'dodecahedron_plus', name: 'Dodeca+', panels: 12, description: '12 panels · 110 control points' },
  { id: 'dodecahedron_ultra', name: 'Dodeca+ Ultra', panels: 12, description: '12 panels · 230 control points' },
  { id: 'truncated_octahedron', name: 'Truncated Octa', panels: 14, description: '6 squares + 8 hexagons' },
  { id: 'truncated_octahedron_plus', name: 'Truncated Octa+', panels: 14, description: '14 panels · extra control points' },
  { id: 'truncated_octahedron_ultra', name: 'Truncated Octa+ Ultra', panels: 14, description: '14 panels · even more control points' },
  { id: 'icosahedron', name: 'Icosahedron', panels: 20, description: '20 triangular panels' },
  { id: 'icosahedron_plus', name: 'Icosahedron+', panels: 20, description: '20 panels · 102 control points' },
  { id: 'truncated_icosahedron', name: 'Classic 32', panels: 32, description: 'Pentagons + hexagons' },
  { id: 'truncated_icosahedron_plus', name: 'Classic 32+', panels: 32, description: '32 panels · 150 control points' },
  { id: 'truncated_icosahedron_ultra', name: 'Classic 32+ Ultra', panels: 32, description: '32 panels · extra control points' },
  { id: 'star_ball', name: 'Star Ball', panels: 32, description: '12 stars + 20 hexagons · Champions-inspired', pro: true },
  { id: 'star_ball_plus', name: 'Star Ball+', panels: 32, description: '12 stars + 20 hexagons · extra control points', pro: true },
  { id: 'stripes_and_spots', name: 'Stripes & Spots', panels: 14, description: '2 round spots + 12 stripes · pole axis vertical', pro: true },
  { id: 'stripes_and_spots_front', name: 'Front Spot', panels: 14, description: '2 round spots + 6 horizontal bands wrapping the ball', pro: true },
];

// Templates whose flat panels are too large to look spherical without help.
// These get:
//  - domed 3D display (fan-subdivided + re-projected to sphere)
//  - azimuthal-equidistant flattening for SVG export, so cut leather panels
//    curl back into a proper sphere when stitched.
export const CURVED_TEMPLATES = new Set([
  'trionda',
  'cube',
  'brazuca',
  'octahedron',
  'octahedron_plus',
  'octahedron_ultra',
  'dodecahedron',
  'dodecahedron_plus',
  'dodecahedron_ultra',
  'truncated_octahedron',
  'truncated_octahedron_plus',
  'truncated_octahedron_ultra',
  'icosahedron',
  'icosahedron_plus',
  'truncated_icosahedron_plus',
  'truncated_icosahedron_ultra',
  'star_ball',
  'star_ball_plus',
  'stripes_and_spots',
  'stripes_and_spots_front',
]);
